import axiosInstance from './api';

const getApiErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  return error.message || error?.error?.message || error?.data?.message || fallback;
};

/**
 * GET /admin/events
 * @param {{ limit?: number, page?: number, published?: boolean, types?: string, event_type?: string, search?: string }} params
 */
export const getEvents = async (params = {}) => {
  try {
    const query = {};

    if (params.limit != null) query.limit = params.limit;
    if (params.page != null) query.page = params.page;
    if (params.published != null) query.published = params.published;
    if (params.types) query.types = params.types;
    if (params.event_type) query.event_type = params.event_type;
    if (params.search) query.search = params.search;

    const response = await axiosInstance.get('/admin/events', { params: query });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to load events'),
      error,
    };
  }
};

/**
 * GET /admin/event-metrics
 */
export const getEventMetrics = async () => {
  try {
    const response = await axiosInstance.get('/admin/event-metrics');
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to fetch event metrics'),
      error,
    };
  }
};

export const getEventById = async (eventId) => {
  try {
    const response = await axiosInstance.get(`/admin/events/${eventId}`);

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to load event'),
      error,
    };
  }
};

export const addEvent = async (formData) => {
  try {
    const response = await axiosInstance.post('/admin/add-event', formData);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to create event'),
      error,
    };
  }
};

/**
 * PUT /admin/edit-event/:id
 * @param {string|number} eventId
 * @param {FormData} formData
 */
export const editEvent = async (eventId, formData) => {
  try {
    const response = await axiosInstance.put(`/admin/edit-event/${eventId}`, formData);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to update event'),
      error,
    };
  }
};

/**
 * PATCH /admin/events/:id/published (no request body)
 * @param {string|number} eventId
 */
export const updateEventPublished = async (eventId) => {
  try {
    const response = await axiosInstance.patch(`/admin/events/${eventId}/published`);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to update published status'),
      error,
    };
  }
};

/**
 * PATCH /admin/events/:id/status
 * @param {string|number} eventId
 * @param {{status: 'approved'|'request_changes', feedback?: object}} payload
 */
export const updateEventModerationStatus = async (eventId, payload) => {
  try {
    const response = await axiosInstance.patch(`/admin/events/${eventId}/status`, payload);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to update moderation status'),
      error,
    };
  }
};

/**
 * POST /admin/events/:eventId/bookings/:bookingId/refund
 * @param {string|number} eventId
 * @param {string|number} bookingId
 */
export const refundEventBooking = async (eventId, bookingId) => {
  try {
    const response = await axiosInstance.post(
      `/admin/events/${eventId}/bookings/${bookingId}/refund`,
    );
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to process refund'),
      error,
    };
  }
};

/**
 * POST /admin/events/:eventId/attendees
 * @param {string|number} eventId
 * @param {{ attendees: Array<{ name: string, email: string, phoneNumber?: string|null }> }} payload
 */
export const addEventAttendees = async (eventId, payload) => {
  try {
    const response = await axiosInstance.post(`/admin/events/${eventId}/attendees`, payload);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to add attendees'),
      error,
    };
  }
};

/**
 * DELETE /admin/events/:eventId/attendees/:attendeeId
 * @param {string|number} eventId
 * @param {string|number} attendeeId
 */
export const deleteEventAttendee = async (eventId, attendeeId) => {
  try {
    const response = await axiosInstance.delete(`/admin/events/${eventId}/attendees/${attendeeId}`);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to remove attendee'),
      error,
    };
  }
};

/**
 * DELETE /admin/events/:id
 * @param {string|number} eventId
 */
export const deleteEvent = async (eventId) => {
  try {
    const response = await axiosInstance.delete(`/admin/events/${eventId}`);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to delete event'),
      error,
    };
  }
};

/** GET /admin/events/:eventId/reviews — pending and approved reviews for one event. */
export const getEventReviews = async (eventId) => {
  try {
    const response = await axiosInstance.get(`/admin/events/${eventId}/reviews`);

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? null,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to load event reviews'),
      error,
    };
  }
};

/** PATCH /admin/events/:eventId/reviews/:reviewId/status */
export const updateEventReviewStatus = async (eventId, reviewId, status) => {
  try {
    const response = await axiosInstance.patch(
      `/admin/events/${eventId}/reviews/${reviewId}/status`,
      { status },
    );

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? 'Review status updated',
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to update review status'),
      error,
    };
  }
};
